/**
 * Warehouse API Service Layer
 *
 * Handles all warehouse-related API calls with data transformation
 * between frontend and backend formats.
 */

import api from './api';
import {
  Warehouse,
  WarehouseContact,
  WarehouseDocument,
  WarehouseNote,
  WarehousePayment,
  WarehouseReminder
} from '../types/warehouse';

// ==================== Backend Types ====================

interface BackendWarehouse {
  id: number;
  warehouse_code: string;
  warehouse_name: string;
  short_name?: string;
  warehouse_type?: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  warehouse_status?: string;
  operating_hours?: string;
  services?: string[];
  special_equipment?: string[];
  equipment_available?: string[];
  total_square_feet?: number;
  total_pallet_positions?: number;
  used_pallet_positions?: number;
  is_preferred?: boolean;
  notes?: string;
  special_instructions?: string;
  total_orders?: number;
  total_revenue?: number;
  average_order_value?: number;
  total_shipments?: number;
  monthly_throughput?: number;
  average_processing_time?: number;
  contacts?: WarehouseContact[];
  created_at?: string;
  updated_at?: string;
}

interface WarehouseListResponse {
  success: boolean;
  data: BackendWarehouse[];
  total: number;
  limit: number;
  offset: number;
}

export interface DuplicateWarehouseMatch {
  /** 'same' = same street and same city/ZIP; 'street-only' = same street, different locality */
  level: 'same' | 'street-only';
  message: string;
  warehouse: {
    id: string;
    warehouseCode?: string;
    warehouseName: string;
    street?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    warehouseStatus?: string;
  };
}

interface WarehouseListParams {
  limit?: number;
  offset?: number;
  status?: string;
  warehouse_type?: string;
  search?: string;
  is_preferred?: boolean;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

// ==================== Transform Functions ====================

/**
 * Transform backend warehouse data to frontend format
 * Note: Backend may return data in camelCase format with pre-built objects (address, capacity)
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function transformFromBackend(data: any): Warehouse {
  // Handle address - backend may return as object or flat fields
  let address: Warehouse['address'];
  if (data.address && typeof data.address === 'object') {
    // Backend returned pre-built address object
    address = {
      street: data.address.street || '',
      city: data.address.city || '',
      state: data.address.state || '',
      zipCode: data.address.zipCode || '',
      country: data.address.country || 'USA',
    };
  } else {
    // Backend returned flat fields (snake_case)
    address = {
      street: data.address || '',
      city: data.city || '',
      state: data.state || '',
      zipCode: data.zip_code || '',
      country: data.country || 'USA',
    };
  }

  // Handle capacity - backend may return as object or flat fields
  let capacity: Warehouse['capacity'];
  if (data.capacity && typeof data.capacity === 'object') {
    // Backend returned pre-built capacity object
    capacity = {
      totalSquareFeet: data.capacity.totalSquareFeet || 0,
      totalPalletPositions: data.capacity.totalPalletPositions || 0,
      usedPalletPositions: data.capacity.usedPalletPositions || 0,
    };
  } else {
    // Backend returned flat fields (snake_case)
    capacity = {
      totalSquareFeet: data.total_square_feet || 0,
      totalPalletPositions: data.total_pallet_positions || 0,
      usedPalletPositions: data.used_pallet_positions || 0,
    };
  }

  return {
    id: String(data.id),
    warehouseCode: data.warehouseCode || data.warehouse_code || '',
    warehouseName: data.warehouseName || data.warehouse_name || '',
    shortName: data.shortName || data.short_name,
    warehouseType: (data.warehouseType || data.warehouse_type || 'Other') as Warehouse['warehouseType'],
    address,
    contacts: data.contacts || [],
    warehouseStatus: (data.warehouseStatus || data.warehouse_status || 'active') as Warehouse['warehouseStatus'],
    operatingHours: data.operatingHours || data.operating_hours || '',
    services: data.services || [],
    equipmentAvailable: data.equipmentAvailable || data.special_equipment || data.equipment_available || [],
    capacity,
    createdDate: data.createdDate || data.created_at || new Date().toISOString(),
    lastUpdated: data.lastUpdated || data.updated_at || new Date().toISOString(),
    totalShipments: data.totalShipments || data.total_shipments || 0,
    monthlyThroughput: data.monthlyThroughput || data.monthly_throughput || 0,
    averageProcessingTime: data.averageProcessingTime || data.average_processing_time || 0,
    isPreferred: data.isPreferred ?? data.is_preferred ?? false,
    appointmentRequired: data.appointmentRequired ?? data.appointment_required ?? true,
    specialInstructions: data.specialInstructions || data.notes || data.special_instructions,
    totalOrders: data.totalOrders || data.total_orders,
    totalRevenue: data.totalRevenue || data.total_revenue,
    averageOrderValue: data.averageOrderValue || data.average_order_value,
  };
}

/**
 * Transform frontend warehouse data to backend format for create/update
 */
function transformToBackend(data: Partial<Warehouse>): Partial<BackendWarehouse> {
  const result: Partial<BackendWarehouse> = {};

  if (data.warehouseCode !== undefined) result.warehouse_code = data.warehouseCode;
  if (data.warehouseName !== undefined) result.warehouse_name = data.warehouseName;
  if (data.shortName !== undefined) result.short_name = data.shortName;
  if (data.warehouseType !== undefined) result.warehouse_type = data.warehouseType;
  if (data.warehouseStatus !== undefined) result.warehouse_status = data.warehouseStatus;
  if (data.operatingHours !== undefined) result.operating_hours = data.operatingHours;
  if (data.services !== undefined) result.services = data.services;
  if (data.equipmentAvailable !== undefined) result.special_equipment = data.equipmentAvailable;
  if (data.isPreferred !== undefined) result.is_preferred = data.isPreferred;
  if (data.appointmentRequired !== undefined) (result as Record<string, unknown>).appointment_required = data.appointmentRequired;
  if (data.specialInstructions !== undefined) result.notes = data.specialInstructions;

  if (data.address) {
    result.address = data.address.street;
    result.city = data.address.city;
    result.state = data.address.state;
    result.zip_code = data.address.zipCode;
    result.country = data.address.country;
  }

  if (data.capacity) {
    result.total_square_feet = data.capacity.totalSquareFeet;
    result.total_pallet_positions = data.capacity.totalPalletPositions;
    result.used_pallet_positions = data.capacity.usedPalletPositions;
  }

  // Pass through contacts array for create/update
  if (data.contacts) {
    result.contacts = data.contacts;
  }

  return result;
}

// ==================== API Methods ====================

export const warehouseApi = {
  /**
   * Get all warehouses with optional filters
   */
  async getAll(params?: WarehouseListParams): Promise<{ data: Warehouse[]; total: number }> {
    const response = await api.get('/warehouse-clients', { params });
    const backendData = response.data as WarehouseListResponse;

    return {
      data: (backendData.data || []).map(transformFromBackend),
      total: backendData.total || 0,
    };
  },

  /**
   * Get a single warehouse by ID
   */
  async getById(id: string): Promise<Warehouse> {
    const response = await api.get(`/warehouse-clients/${id}`);
    return transformFromBackend(response.data);
  },

  /**
   * Create a new warehouse
   */
  async create(data: Omit<Warehouse, 'id'>): Promise<Warehouse> {
    const backendData = transformToBackend(data);
    const response = await api.post('/warehouse-clients', backendData);
    return transformFromBackend(response.data);
  },

  /**
   * Update an existing warehouse
   */
  async update(id: string, data: Partial<Warehouse>): Promise<Warehouse> {
    const backendData = transformToBackend(data);
    const response = await api.put(`/warehouse-clients/${id}`, backendData);
    return transformFromBackend(response.data);
  },

  /**
   * Delete a warehouse
   */
  async delete(id: string): Promise<void> {
    await api.delete(`/warehouse-clients/${id}`);
  },

  /**
   * Check whether an address is already taken by another warehouse in this
   * tenant. `level: 'same'` is a hard duplicate (the backend refuses to create
   * it); `'street-only'` is a warning worth showing but not blocking.
   */
  async checkAddress(
    address: { street: string; city?: string; state?: string; zipCode?: string },
    excludeId?: string
  ): Promise<DuplicateWarehouseMatch | null> {
    const params: Record<string, string> = { street: address.street };
    if (address.city) params.city = address.city;
    if (address.state) params.state = address.state;
    if (address.zipCode) params.zipCode = address.zipCode;
    if (excludeId) params.excludeId = excludeId;

    const response = await api.get('/warehouse-clients/check-address', { params });
    return (response.data as { duplicate: DuplicateWarehouseMatch | null }).duplicate || null;
  },

  /**
   * Get next available warehouse code
   */
  async getNextCode(): Promise<string> {
    const response = await api.get('/warehouse-clients/next-code');
    return response.data.warehouseCode || '';
  },

  /**
   * Get warehouse summary statistics
   */
  async getSummary(id: string): Promise<Record<string, unknown>> {
    const response = await api.get(`/warehouse-clients/${id}/summary`);
    return response.data;
  },

  /**
   * Get warehouse statistics
   */
  async getStats(): Promise<Record<string, unknown>> {
    const response = await api.get('/warehouse-clients/stats');
    return response.data;
  },

  // ==================== Contacts API ====================

  async getContacts(warehouseId: string): Promise<{ success: boolean; data: WarehouseContact[] }> {
    const response = await api.get(`/warehouse-clients/${warehouseId}/contacts`);
    return response.data;
  },

  // ==================== Documents API ====================

  async getDocuments(warehouseId: string): Promise<{ success: boolean; data: WarehouseDocument[] }> {
    const response = await api.get(`/warehouse-clients/${warehouseId}/documents`);
    return response.data;
  },

  // ==================== Notes API ====================

  async getNotes(warehouseId: string): Promise<{ success: boolean; data: WarehouseNote[] }> {
    const response = await api.get(`/warehouse-clients/${warehouseId}/notes`);
    return response.data;
  },

  async createNote(warehouseId: string, noteData: Omit<WarehouseNote, 'id' | 'warehouseId'>): Promise<{ success: boolean; data: WarehouseNote }> {
    const response = await api.post(`/warehouse-clients/${warehouseId}/notes`, noteData);
    return response.data;
  },

  async updateNote(noteId: string, noteData: Partial<WarehouseNote>): Promise<{ success: boolean; data: WarehouseNote }> {
    const response = await api.put(`/warehouse-clients/notes/${noteId}`, noteData);
    return response.data;
  },

  async deleteNote(noteId: string): Promise<{ success: boolean }> {
    const response = await api.delete(`/warehouse-clients/notes/${noteId}`);
    return response.data;
  },

  // ==================== Payments API ====================

  async getPayments(warehouseId: string): Promise<{ success: boolean; data: WarehousePayment[] }> {
    const response = await api.get(`/warehouse-clients/${warehouseId}/payments`);
    return response.data;
  },

  async upsertPayment(warehouseId: string, paymentData: Omit<WarehousePayment, 'id' | 'warehouseId'>): Promise<{ success: boolean; data: WarehousePayment }> {
    const response = await api.post(`/warehouse-clients/${warehouseId}/payments`, paymentData);
    return response.data;
  },

  // ==================== Reminders API ====================

  async getReminders(warehouseId: string): Promise<{ success: boolean; data: WarehouseReminder[] }> {
    const response = await api.get(`/warehouse-clients/${warehouseId}/reminders`);
    return response.data;
  },

  async createReminder(warehouseId: string, reminderData: Omit<WarehouseReminder, 'id' | 'warehouseId'>): Promise<{ success: boolean; data: WarehouseReminder }> {
    const response = await api.post(`/warehouse-clients/${warehouseId}/reminders`, reminderData);
    return response.data;
  },

  async updateReminder(reminderId: string, reminderData: Partial<WarehouseReminder>): Promise<{ success: boolean; data: WarehouseReminder }> {
    const response = await api.put(`/warehouse-clients/reminders/${reminderId}`, reminderData);
    return response.data;
  },

  async deleteReminder(reminderId: string): Promise<{ success: boolean }> {
    const response = await api.delete(`/warehouse-clients/reminders/${reminderId}`);
    return response.data;
  },
};

